import { BffUnauthenticatedError, bffFetch, type ReadLane } from "@/shared/api/client";
import { getIdentity } from "@/shared/api/identity";
import {
  applyAmountBasis,
  mapOptimizeResponse,
  mapTransactionSet,
  type RawTransactionSet,
  type RawV2OptimizeResponse,
} from "@/domain/quant/frontier-map";
import { buildOptimizeRequestBody, type OptimizeBasis } from "@/domain/quant/optimize-request";
import {
  buildAnalysisRequestBody,
  defaultReportTemplate,
  offeredReportTemplates,
  emptyTransactionSetRefused,
  idFromLocation,
  quantTaskRaceLost,
  type DeckTemplate,
  type ReportTemplate,
} from "@/domain/quant/suggested-portfolio";
import { lastOptimize } from "@/domain/quant/quant-client";
import { writeOnce, type Lookup, type SendOutcome } from "@/domain/quant/landed-write";
import type { ProspectPortfolio, TransactionSetResponse } from "@/domain/quant/types";

const OPTIMIZE_STAGE = "optimize" as const;

export const MATERIALIZATION_STAGES = [
  "transaction-set",
  "quantify",
  "prospect-analysis",
  "portfolio-analysis",
  "templates",
  "suggested-portfolio",
] as const;

const PORTFOLIO_PREPARATION_STAGES = [OPTIMIZE_STAGE, ...MATERIALIZATION_STAGES] as const;

type PortfolioPreparationStep = (typeof PORTFOLIO_PREPARATION_STAGES)[number];
type OptimizeStep = typeof OPTIMIZE_STAGE;
export type PortfolioMaterializationStep = (typeof MATERIALIZATION_STAGES)[number];

interface PortfolioPreparationFailure {
  ok: false;
  step: PortfolioPreparationStep;
  status: number | null;
  authExpired: boolean;
  detail: string;
  reason?: "no-transactions" | "unconfirmed";
}

interface SuggestedPortfolioArtifactIds {
  quantPortfolioId: string;
  prospectSetId: string;
  prospectPortfolioId: number;
  transactionSetId: string;
  prospectAnalysisId: string;
  portfolioAnalysisId: string;
  deckTemplateId: string;
  suggestedPortfolioId: string;
}

export type PortfolioPreparationResult =
  | {
      ok: true;
      ids: SuggestedPortfolioArtifactIds;
      transactionSet: TransactionSetResponse | null;
    }
  | PortfolioPreparationFailure;

export type DeckTemplateRead = { ok: true; template: DeckTemplate } | PortfolioPreparationFailure;

interface FrontierRun {
  prospectSetId: string;
  startValue: number | null;
  points: ProspectPortfolio[];
}

export type OptimizeFrontierResult = { ok: true; run: FrontierRun } | PortfolioPreparationFailure;

export interface SuggestionPick {
  quantPortfolioId: string;
  prospectSetId: string;
  prospectPortfolioId: number;
  transactionSetId?: string | null;
  portfolioAnalysisId?: string | null;
  prospectAnalysisId?: string | null;
  startValue: number | null;
  basis: OptimizeBasis;
  owner?: () => Promise<SuggestionOwner | null>;
}

export interface SuggestionOwner {
  kind: "individual" | "corporation";
  clientLinkId: string;
}

export type TransactionSetStep = "transaction-set" | "quantify";

interface TransactionSetFailure {
  ok: false;
  step: TransactionSetStep;
  status: number | null;
  authExpired: boolean;
  detail: string;
}

export type QuantifiedTransactionSetResult =
  | { ok: true; set: TransactionSetResponse; transactionSetId: string }
  | TransactionSetFailure;

const encodePathSegment = encodeURIComponent;
const sleep = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

type BffRequestResult =
  | { response: Response; authExpired: false }
  | { response: null; authExpired: true };

async function request(
  path: string,
  method: "GET" | "POST" | "DELETE",
  body?: unknown,
): Promise<BffRequestResult> {
  try {
    const response = await bffFetch(path, {
      method,
      ...(body !== undefined ? { json: body } : {}),
    });
    return { response, authExpired: false };
  } catch (error) {
    if (error instanceof BffUnauthenticatedError) {
      return { response: null, authExpired: true };
    }
    throw error;
  }
}

const expired = (step: PortfolioPreparationStep): PortfolioPreparationFailure => ({
  ok: false,
  step,
  status: 401,
  authExpired: true,
  detail: "the BFF session is gone",
});

async function failureFrom(
  step: PortfolioPreparationStep,
  response: Response,
): Promise<PortfolioPreparationFailure> {
  let bodyText = "";
  try {
    bodyText = (await response.text()).trim().slice(0, 500);
  } catch {}
  return {
    ok: false,
    step,
    status: response.status,
    authExpired: false,
    detail: `${response.status} ${response.statusText}${bodyText ? ` — ${bodyText}` : ""}`,
  };
}

async function createWithLocation(
  step: PortfolioPreparationStep,
  path: string,
  body: unknown,
  attempts: number,
): Promise<{ id: string } | PortfolioPreparationFailure> {
  let last: PortfolioPreparationFailure | null = null;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    const result = await request(path, "POST", body);
    if (result.authExpired) return expired(step);
    const { response } = result;
    if (response.ok) {
      const id = idFromLocation(response.headers.get("location"));
      if (id) return { id };
      return {
        ok: false,
        step,
        status: response.status,
        authExpired: false,
        detail: `${response.status} without a Location header — the created id is unrecoverable`,
      };
    }
    last = await failureFrom(step, response);
    if (!quantTaskRaceLost(response.status, last.detail) || attempt === attempts) return last;
    await sleep(attempt * 1000);
  }
  return last as PortfolioPreparationFailure;
}

export async function optimizeFrontier(
  quantPortfolioId: string,
  basis: OptimizeBasis,
  onStep: (step: OptimizeStep) => void,
): Promise<OptimizeFrontierResult> {
  onStep("optimize");
  const body = buildOptimizeRequestBody(basis);
  const landed = await writeOnce<FrontierRun, PortfolioPreparationFailure, true>({
    key: `optimize:${quantPortfolioId}:${JSON.stringify(body)}`,
    group: `optimize:${quantPortfolioId}`,
    settleMs: OPTIMIZE_SETTLES_MS,
    unknownSettles: true,
    send: (pending) => sendOptimize(quantPortfolioId, body, pending),
    lookup: (startedAt) => landedOptimize(quantPortfolioId, startedAt),
  });
  if (!landed.ok) {
    return landed.unconfirmed ? { ...landed.failure, reason: "unconfirmed" } : landed.failure;
  }
  seenSetsOf(quantPortfolioId).add(landed.value.prospectSetId);
  return { ok: true, run: landed.value };
}

async function sendOptimize(
  quantPortfolioId: string,
  body: unknown,
  pending: (proof: true) => void,
): Promise<SendOutcome<FrontierRun, PortfolioPreparationFailure>> {
  pending(true);
  const sent = await post(
    "optimize",
    `v1/quant-portfolio/${encodePathSegment(quantPortfolioId)}/optimize`,
    body,
  );
  if (sent.kind !== "done") return sent;
  let frontier: ReturnType<typeof mapOptimizeResponse>;
  try {
    frontier = mapOptimizeResponse((await sent.value.json()) as RawV2OptimizeResponse);
  } catch {
    return {
      kind: "unclear",
      failure: {
        ok: false,
        step: "optimize",
        status: null,
        authExpired: false,
        detail: "unparseable optimize response",
      },
    };
  }
  const run = frontierRunOf(frontier);
  return "ok" in run ? { kind: "refused", failure: run } : { kind: "done", value: run };
}

async function landedOptimize(
  quantPortfolioId: string,
  startedAt: number,
): Promise<Lookup<FrontierRun>> {
  const stored = await lastOptimize(quantPortfolioId);
  if (stored === null || !madeSince(stored.computedAt, startedAt)) return { kind: "absent" };
  const run = frontierRunOf(stored);
  if ("ok" in run || seenSetsOf(quantPortfolioId).has(run.prospectSetId)) {
    return { kind: "absent" };
  }
  return { kind: "found", value: run };
}

function seenSetsOf(quantPortfolioId: string): Set<string> {
  const known = SEEN_SETS.get(quantPortfolioId);
  if (known) return known;
  const fresh = new Set<string>();
  SEEN_SETS.set(quantPortfolioId, fresh);
  return fresh;
}

function madeSince(stamp: string | null | undefined, startedAt: number): boolean {
  const at = Date.parse(stamp ?? "");
  return !Number.isNaN(at) && at >= startedAt - CLOCK_MARGIN_MS;
}

function frontierRunOf(
  frontier: ReturnType<typeof mapOptimizeResponse>,
): FrontierRun | PortfolioPreparationFailure {
  const prospectSetId = frontier.id === null ? null : String(frontier.id);
  if (!prospectSetId) {
    return {
      ok: false,
      step: "optimize",
      status: null,
      authExpired: false,
      detail: "optimize response carried no set id",
    };
  }
  if (frontier.prospect_portfolios.length === 0) {
    return {
      ok: false,
      step: "optimize",
      status: null,
      authExpired: false,
      detail: "optimize returned an empty frontier",
    };
  }
  return {
    prospectSetId,
    startValue: frontier.start_value,
    points: applyAmountBasis(frontier.prospect_portfolios, frontier.start_value ?? 0),
  };
}

async function post(
  step: PortfolioPreparationStep,
  path: string,
  body: unknown,
): Promise<SendOutcome<Response, PortfolioPreparationFailure>> {
  let result: BffRequestResult;
  try {
    result = await request(path, "POST", body);
  } catch (error) {
    return {
      kind: "unclear",
      failure: {
        ok: false,
        step,
        status: null,
        authExpired: false,
        detail: `no answer: ${error instanceof Error ? error.message : "unknown"}`,
      },
    };
  }
  if (result.authExpired) return { kind: "refused", failure: expired(step) };
  if (result.response.ok) return { kind: "done", value: result.response };
  const failure = await failureFrom(step, result.response);
  return { kind: answerUnclear(result.response.status) ? "unclear" : "refused", failure };
}

function answerUnclear(status: number): boolean {
  return status === 408 || status >= 500;
}

export async function createQuantifiedTransactionSet(
  quantPortfolioId: string,
  prospectSetId: string,
  prospectPortfolioId: number,
  onStep: (step: TransactionSetStep) => void = () => {},
): Promise<QuantifiedTransactionSetResult> {
  onStep("transaction-set");
  const created = await createWithLocation(
    "transaction-set",
    "v1/quant-portfolio/portfolio-transaction-set",
    {
      quant_portfolio_id: String(quantPortfolioId),
      quant_prospect_portfolio_set_id: prospectSetId,
      quant_prospect_portfolio_id: String(prospectPortfolioId),
    },
    1,
  );
  if ("ok" in created) return { ...created, step: "transaction-set" };

  onStep("quantify");
  const quantification = await request(
    `v1/quant-portfolio/portfolio-transaction-set/${encodePathSegment(created.id)}/quantify`,
    "POST",
  );
  if (quantification.authExpired) return { ...expired("quantify"), step: "quantify" };
  if (!quantification.response.ok) {
    return { ...(await failureFrom("quantify", quantification.response)), step: "quantify" };
  }
  try {
    const set = mapTransactionSet((await quantification.response.json()) as RawTransactionSet);
    return { ok: true, set, transactionSetId: created.id };
  } catch {
    return {
      ok: false,
      step: "quantify",
      status: null,
      authExpired: false,
      detail: "unparseable quantify response",
    };
  }
}

export async function createSuggestedPortfolioArtifacts(
  pick: SuggestionPick,
  onStep: (step: PortfolioMaterializationStep) => void,
  deckTemplate: Promise<DeckTemplateRead>,
): Promise<PortfolioPreparationResult> {
  const landed = await writeOnce<SuggestedPortfolio, PortfolioPreparationFailure, SuggestionProof>({
    key: `suggested-portfolio:${pick.quantPortfolioId}:${pick.prospectSetId}:${pick.prospectPortfolioId}`,
    group: `suggested-portfolio:${pick.quantPortfolioId}:${pick.prospectSetId}:${pick.prospectPortfolioId}`,
    settleMs: SUGGESTION_SETTLES_MS,
    unknownSettles: false,
    send: (pending) => sendSuggestedPortfolio(pick, onStep, deckTemplate, pending),
    lookup: (startedAt, proof) => landedSuggestion(pick, proof, startedAt),
  });
  if (landed.ok) return { ok: true, ...landed.value };
  return landed.unconfirmed ? { ...landed.failure, reason: "unconfirmed" } : landed.failure;
}

async function sendSuggestedPortfolio(
  pick: SuggestionPick,
  onStep: (step: PortfolioMaterializationStep) => void,
  deckTemplate: Promise<DeckTemplateRead>,
  pending: (proof: SuggestionProof) => void,
): Promise<SendOutcome<SuggestedPortfolio, PortfolioPreparationFailure>> {
  const { quantPortfolioId, prospectSetId, prospectPortfolioId, basis } = pick;

  let transactionSetId = pick.transactionSetId ?? null;
  let transactionSet: TransactionSetResponse | null = null;
  if (transactionSetId === null) {
    const created = await createQuantifiedTransactionSet(
      quantPortfolioId,
      prospectSetId,
      prospectPortfolioId,
      onStep,
    );
    if (!created.ok) return { kind: "refused", failure: created };
    if (created.set.transactions.length === 0) {
      return {
        kind: "refused",
        failure: {
          ok: false,
          step: "quantify",
          status: null,
          authExpired: false,
          detail: `transaction set ${created.transactionSetId} has no transactions`,
          reason: "no-transactions",
        },
      };
    }
    transactionSetId = created.transactionSetId;
    transactionSet = created.set;
  } else {
    onStep("transaction-set");
    onStep("quantify");
  }

  const analysisBody = buildAnalysisRequestBody(pick.startValue, basis);
  onStep("prospect-analysis");
  const prospectAnalysis = pick.prospectAnalysisId
    ? { id: pick.prospectAnalysisId }
    : await createWithLocation(
        "prospect-analysis",
        `v1/quant-portfolio/prospect-portfolio-set/${encodePathSegment(prospectSetId)}/prospect-portfolio/${encodePathSegment(String(prospectPortfolioId))}/analysis`,
        analysisBody,
        3,
      );
  if ("ok" in prospectAnalysis) return { kind: "refused", failure: prospectAnalysis };

  onStep("portfolio-analysis");
  const portfolioAnalysis = pick.portfolioAnalysisId
    ? { id: pick.portfolioAnalysisId }
    : await createWithLocation(
        "portfolio-analysis",
        `v1/quant-portfolio/${encodePathSegment(quantPortfolioId)}/analysis`,
        analysisBody,
        3,
      );
  if ("ok" in portfolioAnalysis) return { kind: "refused", failure: portfolioAnalysis };

  onStep("templates");
  const selectedTemplate = await deckTemplate;
  if (!selectedTemplate.ok) return { kind: "refused", failure: selectedTemplate };

  onStep("suggested-portfolio");
  const proof: SuggestionProof = {
    quantPortfolioId,
    prospectSetId,
    prospectPortfolioId,
    transactionSetId,
    prospectAnalysisId: prospectAnalysis.id,
    portfolioAnalysisId: portfolioAnalysis.id,
    deckTemplateId: selectedTemplate.template.id,
    transactionSet,
  };
  pending(proof);
  const sent = await post("suggested-portfolio", "v1/quant-portfolio/suggested-portfolio", {
    quant_portfolio_transaction_set_id: transactionSetId,
    portfolio_analysis_id: portfolioAnalysis.id,
    prospect_portfolio_analysis_id: prospectAnalysis.id,
    simple_power_point_template_id: selectedTemplate.template.id,
  });
  if (sent.kind !== "done") {
    const { failure } = sent;
    return failure.status !== null && emptyTransactionSetRefused(failure.status, failure.detail)
      ? { kind: "refused", failure: { ...failure, reason: "no-transactions" } }
      : sent;
  }
  const suggestedPortfolioId = idFromLocation(sent.value.headers.get("location"));
  if (!suggestedPortfolioId) {
    return {
      kind: "unclear",
      failure: {
        ok: false,
        step: "suggested-portfolio",
        status: sent.value.status,
        authExpired: false,
        detail: `${sent.value.status} without a Location header — the created id is unrecoverable`,
      },
    };
  }
  return { kind: "done", value: landedValue(proof, suggestedPortfolioId) };
}

async function landedSuggestion(
  pick: SuggestionPick,
  proof: SuggestionProof,
  startedAt: number,
): Promise<Lookup<SuggestedPortfolio>> {
  const owner = await pick.owner?.();
  if (!owner) return { kind: "unknown" };
  const read = await readJson(
    `v1/quant-portfolio/suggested-portfolio?${owner.kind}_client_id=${encodePathSegment(owner.clientLinkId)}`,
    "suggested-portfolio",
  );
  if (!read.ok || !Array.isArray(read.json)) return { kind: "unknown" };
  const row = (read.json as SuggestionRowWire[]).find(
    (candidate) =>
      String(candidate.quant_portfolio_transaction_set_id) === proof.transactionSetId &&
      madeSince(candidate.awaiting_approval_at, startedAt),
  );
  if (!row || row.id == null) return { kind: "absent" };
  return { kind: "found", value: landedValue(proof, String(row.id)) };
}

function landedValue(proof: SuggestionProof, suggestedPortfolioId: string): SuggestedPortfolio {
  const { transactionSet, ...ids } = proof;
  return { ids: { ...ids, suggestedPortfolioId }, transactionSet };
}

interface SuggestionRowWire {
  id?: unknown;
  quant_portfolio_transaction_set_id?: unknown;
  awaiting_approval_at?: string | null;
}

type SuggestionProof = Omit<SuggestedPortfolioArtifactIds, "suggestedPortfolioId"> & {
  transactionSet: TransactionSetResponse | null;
};

interface SuggestedPortfolio {
  ids: SuggestedPortfolioArtifactIds;
  transactionSet: TransactionSetResponse | null;
}

export function readReportTemplates(): Promise<ReportTemplatesRead> {
  if (templatesRead) return templatesRead;
  const read = fetchReportTemplates();
  const forget = () => {
    if (templatesRead === read) templatesRead = null;
  };
  templatesRead = read;
  read.then((result) => {
    if (!result.ok) forget();
  }, forget);
  return read;
}

async function fetchReportTemplates(): Promise<ReportTemplatesRead> {
  const templateResponse = await request("v1/quant-portfolio/simple-power-point-templates", "GET");
  if (templateResponse.authExpired) return expired("templates");
  if (!templateResponse.response.ok) {
    return failureFrom("templates", templateResponse.response);
  }
  let templates: DeckTemplate[];
  try {
    const json = (await templateResponse.response.json()) as unknown;
    templates = Array.isArray(json) ? (json as DeckTemplate[]) : [];
  } catch {
    templates = [];
  }
  return { ok: true, templates: offeredReportTemplates(templates) };
}

export function reportTemplate(chosenId: string | null): Promise<DeckTemplateRead> {
  if (chosenId) return Promise.resolve({ ok: true, template: { id: chosenId } });
  return advisorReportTemplate();
}

export async function advisorReportTemplate(): Promise<DeckTemplateRead> {
  const read = await readReportTemplates();
  if (!read.ok) return read;
  const template = await firmReportTemplate(read.templates);
  if (!template) {
    return {
      ok: false,
      step: "templates",
      status: null,
      authExpired: false,
      detail: "the BFF lists none of the Citroneer, Tornby and XYZ report templates",
    };
  }
  return { ok: true, template };
}

export async function firmReportTemplate(
  offered: readonly ReportTemplate[],
): Promise<ReportTemplate | null> {
  return defaultReportTemplate(offered, await advisorFirmName());
}

async function advisorFirmName(): Promise<string | null> {
  try {
    return (await getIdentity()).advisorProfile?.corporationName ?? null;
  } catch (error) {
    console.error(
      `report template: the advisor's firm could not be read, so Citroneer's is used — ${
        error instanceof Error ? error.message : "unknown"
      }`,
    );
    return null;
  }
}

let templatesRead: Promise<ReportTemplatesRead> | null = null;

export type ReportTemplatesRead =
  | { ok: true; templates: ReportTemplate[] }
  | PortfolioPreparationFailure;

type QuantReadStep =
  | "suggested-portfolio"
  | "prospect-portfolio-set"
  | "portfolio-transaction-set"
  | "portfolio";

interface QuantReadFailure {
  ok: false;
  step: QuantReadStep;
  detail: string;
}

export type QuantRead = { ok: true; json: unknown } | QuantReadFailure;

async function readJson(
  path: string,
  step: QuantReadStep,
  lane: ReadLane = {},
): Promise<QuantRead> {
  const res = await bffFetch(path, lane);
  if (!res.ok) return { ok: false, step, detail: `${res.status} ${res.statusText}` };
  try {
    return { ok: true, json: (await res.json()) as unknown };
  } catch {
    return { ok: false, step, detail: "unparseable response body" };
  }
}

export function readSuggestedPortfolio(suggestedPortfolioId: string): Promise<QuantRead> {
  return readJson(
    `v1/quant-portfolio/suggested-portfolio/${encodePathSegment(suggestedPortfolioId)}`,
    "suggested-portfolio",
  );
}

export function readProspectPortfolioSet(prospectSetId: string): Promise<QuantRead> {
  return readJson(
    `v1/quant-portfolio/prospect-portfolio-set/${encodePathSegment(prospectSetId)}`,
    "prospect-portfolio-set",
  );
}

export function readTransactionSet(transactionSetId: string, lane?: ReadLane): Promise<QuantRead> {
  return readJson(
    `v1/quant-portfolio/portfolio-transaction-set/${encodePathSegment(transactionSetId)}`,
    "portfolio-transaction-set",
    lane,
  );
}

export function readQuantPortfolio(quantPortfolioId: string, lane?: ReadLane): Promise<QuantRead> {
  return readJson(`v1/quant-portfolio/${encodePathSegment(quantPortfolioId)}`, "portfolio", lane);
}

const SEEN_SETS = new Map<string, Set<string>>();
const CLOCK_MARGIN_MS = 2 * 60_000;
const OPTIMIZE_SETTLES_MS = 7 * 60_000;
const SUGGESTION_SETTLES_MS = 11 * 60_000;
